import type { Project } from "../types";
import { asset } from "../lib/asset";
import { useReveal } from "../hooks/useReveal";
import { useI18n } from "../i18n/LanguageContext";
import { Icon } from "./Icon";
import styles from "./cards.module.css";

interface ProjectCardProps {
  project: Project;
  onOpen: (project: Project) => void;
}

export function ProjectCard({ project, onOpen }: ProjectCardProps) {
  const { t, ui } = useI18n();
  const reveal = useReveal<HTMLButtonElement>();

  const categoryLabel =
    project.category === "commercial"
      ? ui.portfolio.badges.commercial
      : ui.portfolio.badges.openSource;

  return (
    <button
      type="button"
      className={styles.card}
      ref={reveal}
      onClick={() => onOpen(project)}
      aria-label={ui.a11y.openDetails.replace("{title}", project.title)}
    >
      <div className={styles.media}>
        <div className={styles.badges}>
          <span className={`${styles.badge} ${styles.badgeAccent}`}>
            {project.year}
          </span>
          <span className={styles.badge}>{categoryLabel}</span>
        </div>
        <img src={asset(project.image)} alt={project.title} loading="lazy" />
      </div>

      <div className={styles.body}>
        <span className={styles.platform}>{t(project.platform)}</span>
        <h3 className={styles.title}>{project.title}</h3>
        <p className={styles.summary}>{t(project.summary)}</p>

        <div className={styles.tags}>
          {project.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="chip">
              {tag}
            </span>
          ))}
        </div>

        <span className={styles.more}>
          {ui.portfolio.viewDetails}
          <Icon name="external" size={15} />
        </span>
      </div>
    </button>
  );
}
